import capitalize from 'lodash/capitalize';
import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';
import moment from 'moment';

import {openUserSidebar} from 'common/widgets/user-sidebar';
import {extend} from 'common/util';
import {parseNumber, parseNumberRange} from 'common/report/util/sorting/header-sorting';
import {
  BOOLEAN_OPERATOR_NEGATION_MAP,
  getDisplayOptions,
  mapObjectKeys,
  NUMBER_OPERATOR_NEGATION_MAP,
  STRING_OPERATOR_NEGATION_MAP,
} from 'common/report/insights/util';
import {
  ChartType,
  DownloadMode,
  PRIMITIVE_MATH_TYPES,
  ProjectFeatureFlag,
  PropertyType,
  ReportName,
  ResourceType,
  SPECIAL_EVENTS,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {
  BooleanOperator,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {Clause, FilterClause, GroupClause, insightsGroupProperties} from 'common/report/insights/models/clause';
import {computeMetricClauseByKey} from 'common/report/insights/util/tooltip';
import {generateTsxRenderer} from 'common/util/tsx-jade-adapter';
import {currentProjectStore} from 'common/report/stores/project-details';
import {findEventDefinitionByDisplayName} from 'common/report/stores/event-definitions';
import {useStore} from 'common/util/component-helpers';
import {EmptyReportHero, NoDataHero} from 'common/widgets/warning-hero/presets';
import {
  getDownloadDisplayPreferences,
  getDownloadMode,
  getEventsReportUrl,
  getProjectId,
  getReportUrl,
  getWorkspaceId,
} from 'common/report/util';
import {getFilterSection, getPropertyFilters, getPropertyFiltersOperator} from 'common/report/insights/util/filters';
import {getEntries} from 'common/report/insights/util/events';
import {getDateRange, getTimeSection, timestampToXauDateRange} from 'common/report/insights/util/time';
import {createNestedCohort, translateMetricToCohort} from 'common/report/insights/util/translate';
import {
  AnalysisType,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsResourceType,
} from 'common/report/insights/util/types';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {formatDate, getCalendarDate, parseDatetimeRange} from 'common/util/date';
import {
  AbsoluteDate,
  DateRangeType,
  OrderedStandardTimeUnits,
  TimeUnit,
} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {MPProperties, NON_NUMERIC_VALUES_SEGMENT, REMAINING_VALUES_SEGMENT} from 'common/constants';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {renameEvent} from 'common/report/util/events';
import {IronContextRegistry} from 'common/report/context-registry';
import 'common/report/insights/charts';
import {Report} from 'common/report/insights/models/report';
import {ProdTrackingEvent} from 'common/report/insights/tracking';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {openEventsSidebar} from 'common/widgets/events-sidebar';
import {GroupSection} from 'common/report/insights/models/section';
import {INSIGHTS_DATE_RANGE_NEGATION_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {pluralize} from 'common/util/string';
import {ColumnResizeEvent, ColumnWidthOverrides, SortChangeEvent} from 'common/components/data-table/types';
import {ConstantPropertyValues} from 'common/report/util/properties';
import {PopoverActionCallback, PopoverCallback, PopoverOption} from 'common/charts/types';
import {CalculationTypes} from 'common/query-builder/query-entry-section/types';
import {SectionItem} from 'common/components/items-menu/types';

import {convertSegmentForEventsInspector} from './utils';
import css from './index.inline.styl';
import {template} from './template';

const MultiAttributionDisabledTooltipText = `"Attributed by" breakdown not supported.`;

const XAU_MATH_TYPE_TIME_UNITS: Map<XAU_MATH_TYPES, TimeUnit> = new Map([
  [XAU_MATH_TYPES.MAU, TimeUnit.Month],
  [XAU_MATH_TYPES.DAU, TimeUnit.Day],
  [XAU_MATH_TYPES.WAU, TimeUnit.Week],
]);

interface ChartDisplayState {
  report: Report;
}

interface ChartDisplayAttrs {
  'active-data-group-id'?: number;
  'resizing-layout'?: boolean;
  'enable-pivot-view'?: boolean;
  'can-use-view-n'?: boolean;
  'rca-source-id'?: number;
}

interface ChartDisplayProps {
  bookmarkParams: InsightsBookmarkParams;
  queryResults: InsightsQueryResults;
  dataGroups: DataGroup[];
  tableChartColumnWidthOverrides: ColumnWidthOverrides;
}

export type UpdateEvent = CustomEvent<{delta: Partial<InsightsBookmarkParams>}>;

export interface ChartDisplayJsxProps {
  attrs?: ChartDisplayAttrs;
  props: ChartDisplayProps;
  on: {
    update: (ev: UpdateEvent) => void;
    'export-csv': (ev: CustomEvent<void>) => void;
  };
}

export class ChartDisplay extends Component<ChartDisplayState, ChartDisplayAttrs, EmptyObj, void, IronContextRegistry> {
  private _bookmarkParams: InsightsBookmarkParams;
  private _queryResults: InsightsQueryResults;
  private _dataGroups: DataGroup[];
  private _tableChartColumnWidthOverrides: ColumnWidthOverrides = {};

  static get attrsSchema(): AttrsSchema<ChartDisplayAttrs> {
    return {
      ...super.attrsSchema,
      'active-data-group-id': `number`,
      'resizing-layout': `boolean`,
      'enable-pivot-view': `boolean`,
      'can-use-view-n': `boolean`,
      'rca-source-id': `number`,
    };
  }

  set bookmarkParams(params: InsightsBookmarkParams) {
    this._bookmarkParams = params;
    this.update({report: Report.fromBookmarkData(params)});
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  set queryResults(results: InsightsQueryResults) {
    this._queryResults = results;
    this.update();
  }

  get queryResults() {
    return this._queryResults;
  }

  set dataGroups(groups: DataGroup[]) {
    this._dataGroups = groups;
    this.update();
  }

  get dataGroups() {
    return this._dataGroups;
  }

  set tableChartColumnWidthOverrides(overrides: ColumnWidthOverrides) {
    this._tableChartColumnWidthOverrides = overrides;
    this.update();
  }

  get tableChartColumnWidthOverrides() {
    return this._tableChartColumnWidthOverrides;
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`reportLayoutManager` as const, `urlManager` as const, `currentProject` as const],
      helpers: {
        extend,
        renderTsx: generateTsxRenderer({
          NoDataHero,
          EmptyReportHero,
        }),
        getSelectedSource: () => {
          const clause = this.state.report.sections.show.clauses[0];
          return (clause && clause.source) || Clause.RESOURCE_TYPE_EVENTS;
        },
        isDownloadMode: () => getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None,
        enableSegmentsDrillDown: () => {
          const groupClauses = this.state.report.sections.group.clauses;
          if (!groupClauses.length) {
            return !!this.state.report.sections.cohorts.clauses.length;
          }

          if (this.hasMultipleListOfObjectsBreakdowns()) {
            return false;
          }

          return this.helpers.checkGroupClauseDrillDownValidity(groupClauses);
        },
        checkGroupClauseDrillDownValidity: (groupClauses: GroupClause[]): boolean => {
          return groupClauses.reduce((accumulator, groupClause) => {
            if (groupClause instanceof GroupSection) {
              return this.helpers.checkGroupClauseDrillDownValidity(groupClause.clauses);
            }
            const groupType = groupClause.typeCast || groupClause.propertyType;
            const isListOfObjects = groupType === `object` && groupClause.joinPropertyType === PropertyType.List;
            return (
              accumulator &&
              ([`datetime`, `string`, `number`, `boolean`, `list`].includes(groupType) || isListOfObjects)
            );
          }, true);
        },
        isBehavioralSegmentation: () => {
          const groupClauses = this.state.report.sections.group.clauses;
          return groupClauses.some((clause) => clause.behavior);
        },
        isNoDataProject: () => {
          return !(
            currentProjectStore.get(`hasIntegratedArb`, {defaultValue: true}) ||
            currentProjectStore.get(`hasIntegratedEngage`, {defaultValue: true})
          );
        },
        shouldOpenSidebar: () => currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.EventsSidebar),

        isBarChart: () => this.state.report.displayOptions.chartType === ChartType.Bar,
        isLineChart: () => this.state.report.displayOptions.chartType === ChartType.Line,
        isPieChart: () => this.state.report.displayOptions.chartType === ChartType.Pie,
        isTableChart: () => this.state.report.displayOptions.chartType === ChartType.Table,
        isMetricChart: () => this.state.report.displayOptions.chartType === ChartType.Metric,

        getActiveDataGroupDisplayName: () => {
          const activeDataGroupIdStr = String(this.attr(`active-data-group-id`));
          return this.dataGroups?.find((dg) => dg.groupID === activeDataGroupIdStr)?.displayName;
        },
        getChartLabel: () => {
          let label = `Value`;
          if (this.state.report.displayOptions.value === `relative`) {
            label += ` (Percent of the Group)`;
          }
          return label;
        },
        getDisplayOptions: () => {
          const params = this.state.report.toBookmarkData();
          return getDisplayOptions(params.sections, {
            ...params.displayOptions,
            ...this.helpers.getDownloadDisplayPreferences(),
          });
        },
        getDownloadDisplayPreferences: () => getDownloadDisplayPreferences(this.getContext(`urlManager`)),
        getUniqueShowMathTypes: () => new Set(this.state.report.sections.show.clauses.map((clause) => clause.math)),
        getProjectId,
        getWorkspaceId,
        stringifyObjValues: (obj) => mapObjectKeys(obj, JSON.stringify),
        getTableChartData: () => {
          const metricClauseByKey = computeMetricClauseByKey({
            sectionsMetadata: this.queryResults.meta.report_sections,
            dataGroups: this.dataGroups,
            bookmarkParams: this.state.report.toBookmarkData(),
          });

          return {
            result: this.queryResults,
            meta: this.queryResults.meta,
            headers: this.queryResults.headers,
            metricClauseByKey,
          };
        },
        updateBarColumnWidths: (columnWidths) => {
          this.dispatchUpdate({columnWidths: {...this.state.report.columnWidths, bar: columnWidths}});
        },
        updateBarSortConfig: (barSortConfig) => {
          this.dispatchUpdate({
            sorting: {
              ...this.state.report.sorting,
              bar: barSortConfig,
            },
          });
        },

        getBasePopoverActions: (
          segmentPath,
          {forSegmentColumn = false}: {forSegmentColumn?: boolean} = {},
        ): SectionItem[] => {
          const enableSegmentDrillDown =
            this.helpers.enableSegmentsDrillDown() &&
            !segmentPath.some((segment) => [REMAINING_VALUES_SEGMENT].includes(segment));
          const viewUsersDisabledReason = this.segmentUntranslatableReason(segmentPath);
          const viewEventsDisabledReason = this.segmentUntranslatableReasonEvents(segmentPath);

          const dataGroupName = pluralize(this.helpers.getActiveDataGroupDisplayName() || `Users`, true);
          const options = [
            enableSegmentDrillDown && {label: `Filter`, icon: `filter`, value: PopoverOption.Filter},
            enableSegmentDrillDown && {label: `Exclude`, icon: `circle-drop`, value: PopoverOption.Exclude},
            // view users for metric columns/paths
            forSegmentColumn === false && {
              label: `View ${dataGroupName}`,
              icon: `team`,
              value: PopoverOption.ViewUsers,
              ...(viewUsersDisabledReason
                ? {
                    isDisabled: true,
                    tooltip: {text: viewUsersDisabledReason},
                  }
                : {}),
            },
            // view events for metric columns/paths
            forSegmentColumn === false && {
              label: `View Events`,
              icon: `brand-liveview`,
              value: PopoverOption.ViewEvents,
              ...(viewEventsDisabledReason
                ? {
                    isDisabled: true,
                    tooltip: {text: viewEventsDisabledReason},
                  }
                : {}),
            },
          ];

          return options.filter(Boolean);
        },

        lineChartPopoverCallback: (metricKey, segments): SectionItem[] => {
          const {timeUnit} = this.helpers.getDisplayOptions();
          const path = [metricKey, ...segments];
          const disableZoom = this.state.report.isCohortsOverTime() && timeUnit === TimeUnit.Day;
          const options = [
            timeUnit !== TimeUnit.Minute && {
              label: `Zoom In`,
              icon: `search`,
              value: PopoverOption.ZoomIn,
              isDisabled: disableZoom,
            },
            ...this.helpers.getBasePopoverActions(path),
            this.attr(`rca-source-id`) && {
              label: `Root Cause Analysis`,
              icon: `analysis`,
              value: PopoverOption.RCA,
            },
          ];

          return options.filter(Boolean);
        },
        handleLineChartPopoverAction: (ev) => {
          const {pointData, action} = ev.detail;
          const path = [pointData.metricClauseData.metricKey, ...pointData.segments];

          switch (action) {
            case PopoverOption.ViewUsers:
              this.handleLineChartViewUsers(pointData);
              break;
            case PopoverOption.ViewEvents:
              this.handleLineChartViewEvents(pointData);
              break;
            case PopoverOption.ZoomIn:
              this.handleLineChartZoomIn(pointData);
              break;
            case PopoverOption.Filter:
              this.handleTablePopoverFilter(path);
              break;
            case PopoverOption.Exclude:
              this.handleTablePopoverExclude(path);
              break;
            case PopoverOption.RCA:
              this.handleRCAClicked(pointData);
              break;
            default:
              break;
          }
        },

        someSegmentHasMultiAttribution: (segments: string[]): boolean => {
          const clauses = this.getClausesFromSegmentPath(segments);
          return clauses ? clauses.some((clause) => clause.multiAttribution) : false;
        },

        pieChartPopoverCallback: (segmentName: string, segments: string[]): SectionItem[] => {
          if (segmentName === OTHER_SEGMENT_VALUE) {
            return [];
          }

          const isMultiAttribution = this.helpers.someSegmentHasMultiAttribution(segments);
          return [
            {
              label: `View Users`,
              icon: `team`,
              value: PopoverOption.ViewUsers,
              isDisabled: isMultiAttribution,
              tooltip: isMultiAttribution ? {text: MultiAttributionDisabledTooltipText} : undefined,
            },
            {
              label: `View Events`,
              icon: `brand-liveview`,
              value: PopoverOption.ViewEvents,
              isDisabled: isMultiAttribution,
              tooltip: isMultiAttribution ? {text: MultiAttributionDisabledTooltipText} : undefined,
            },
          ];
        },
        handlePieChartPopoverAction: (ev) => {
          const {segment, action, color} = ev.detail;

          switch (action) {
            case PopoverOption.ViewUsers: {
              this.handleViewUsersForSegment(segment, {
                color,
              });
              break;
            }
            case PopoverOption.ViewEvents:
              this.handleViewEventsForSegment(segment);
              break;
          }
        },

        metricChartPopoverCallback: (segmentName: string, segments: string[]): SectionItem[] => {
          const isMultiAttribution = this.helpers.someSegmentHasMultiAttribution(segments);
          if (segmentName === OTHER_SEGMENT_VALUE) {
            return [];
          }
          return [
            {
              label: `View Users`,
              icon: `team`,
              value: PopoverOption.ViewUsers,
              isDisabled: isMultiAttribution,
              tooltip: isMultiAttribution ? {text: MultiAttributionDisabledTooltipText} : undefined,
            },
          ];
        },
        handleMetricChartPopoverAction: (ev) => {
          const {segment, action, color} = ev.detail;

          if (action === PopoverOption.ViewUsers) {
            this.handleViewUsersForSegment(segment, {
              color,
            });
          }
        },
        shouldDisplayTimeComparison: () => !!this.queryResults.time_comparison,
        dataTableSortChange: (options: SortChangeEvent[`detail`]) => {
          this.dispatchUpdate({
            sorting: {
              ...this.bookmarkParams.sorting,
              [ChartType.Table]: options.sorting,
            },
          });
        },
        dataTableColumnResize: (ev: ColumnResizeEvent) => {
          // we should use `this.dispatchUpdate` instead when columnWidths are added to bookmark for table chart
          propagateEvent(this, ev);
        },
        handleExportCsv: (ev) => {
          propagateEvent(this, ev);
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, currentProjectStore);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  tablePopoverCallback: PopoverCallback = (segmentName: string, segmentPath: string[], {forSegmentColumn} = {}) => {
    if (segmentName === OTHER_SEGMENT_VALUE) {
      return [];
    }
    return this.helpers.getBasePopoverActions(segmentPath, {forSegmentColumn});
  };

  createNonRemainingValueFiltersFromCustomGroups(customGroups, groupClause) {
    return customGroups.map((clause) => {
      if (clause.segmentLabel !== REMAINING_VALUES_SEGMENT) {
        return Object.assign({}, groupClause, {
          filterValue: clause.filterValue,
          filterOperator: clause.filterOperator,
        });
      }
    });
  }

  createGroupFromCustomGroups(customGroups, groupClause, segmentLabel) {
    let filteredGroupClause;

    customGroups.forEach((clause) => {
      if (clause.segmentLabel === segmentLabel) {
        filteredGroupClause = Object.assign({}, groupClause, {
          filterValue: clause.filterValue,
          filterOperator: clause.filterOperator,
        });
      }
    });

    return filteredGroupClause;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getFiltersFromPopoverSegments(segment, {makeNumericRangesExclusive = false} = {}) {
    const filters = [];

    const hasCohort = !!this.state.report.sections.cohorts.clauses.length;
    if (hasCohort) {
      const cohortFilter = this.createFilterFromCohort(segment[1], true);
      if (cohortFilter) {
        filters.push(cohortFilter);
      }
    }
    const groupStartSegmentOffset = hasCohort ? 2 : 1;
    const filterSegments = segment.slice(groupStartSegmentOffset, segment.length);
    const groupClauses = this.state.report.sections.group.clauses;
    const metaGroupClauses = this.queryResults.meta.report_sections.group;

    //Build filters for regular breakdowns and value comparisons
    filterSegments.forEach((segmentLabel, idx) => {
      const groupClause: GroupClause = groupClauses[idx];
      const metaGroupClause = metaGroupClauses[idx]?.bookmark;
      const isCustomStringGrouping = Array.isArray(metaGroupClause?.customGroups);
      if (metaGroupClause && Array.isArray(metaGroupClause)) {
        let filteredGroupClause;
        //Only the meta groupClause has the segmentLabel information but we want to use the bookmark groupClause to create the filter
        metaGroupClause.forEach((clause, clauseIndex) => {
          if (clause.segmentLabel === segmentLabel) {
            filteredGroupClause = groupClause[`clauses`][clauseIndex];
          }
        });
        filters.push(this.createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, true));
      } else if (isCustomStringGrouping && segmentLabel === REMAINING_VALUES_SEGMENT) {
        this.createNonRemainingValueFiltersFromCustomGroups(metaGroupClause.customGroups, groupClause).forEach(
          (groupFilter) => {
            filters.push(this.createFilterFromGroup(groupFilter, groupFilter.filterValue, false));
          },
        );
        // Need to also filter out undefined values so we only have remaining values
        filters.push(this.createFilterFromGroup(groupClause, `undefined`, false));
      } else if (isCustomStringGrouping && segmentLabel !== `undefined`) {
        // Handle the customGroups format for Customize Segments - do not handle the undefined case here
        const filteredGroupClause = this.createGroupFromCustomGroups(
          metaGroupClause.customGroups,
          groupClause,
          segmentLabel,
        );
        filters.push(this.createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, true));
      } else if (segmentLabel === NON_NUMERIC_VALUES_SEGMENT) {
        const numFilter = Object.assign({}, groupClause, {
          filterValue: groupClause.value,
          filterOperator: StringOperator.NotSet,
        });
        filters.push(this.createFilterFromGroup(numFilter, numFilter.filterValue, true));
      } else if (metaGroupClause) {
        const filter = this.createFilterFromGroup(groupClause, filterSegments[idx], true);
        if (filter) {
          // for numeric filters coming from a numerical range breakdown, the segment value is inclusive on the left and exclusive on the right, when
          // makeNumericRangesExclusive is set we transform this into an "at least" and "less than" filter to account for that range definition
          if (
            makeNumericRangesExclusive &&
            filter.filterType === PropertyType.Number &&
            filter.filterOperator === NumberOperator.Between
          ) {
            filters.push(
              {
                ...filter,
                filterOperator: NumberOperator.AtLeast,
                filterValue: filter.filterValue[0],
              },
              {
                ...filter,
                filterOperator: NumberOperator.LessThan,
                filterValue: filter.filterValue[1],
              },
            );
          } else {
            filters.push(filter);
          }
        }
      }
    });

    return filters;
  }

  createFilterFromCohort(filterValue, includesFilterValue) {
    const cohortClauses = this.state.report.sections.cohorts.clauses;
    // The assumption here is no two cohorts in the same project share the same name.
    // It is true in today's production.
    const filteredCohortClauses = cohortClauses.filter((clause) => clause.name === filterValue);

    if (filteredCohortClauses.length > 0) {
      const cohortClause = filteredCohortClauses[0];
      const cohortDataset = cohortClause.dataset;
      const cohortProperties = pick(cohortClause, [`id`, `name`, `negated`, `raw_cohort`]);
      return {
        filterOperator: includesFilterValue ? `contains` : `does not contain`,
        filterType: `list`,
        filterValue: [
          {
            cohort: cohortProperties,
          },
        ],
        dataset: cohortDataset,
        resourceType: `events`,
        value: `$cohorts`,
      };
    }
    return null;
  }

  isNoneBucketing(groupClause) {
    if (groupClause.customBucket && `disabled` in groupClause.customBucket) {
      return groupClause.customBucket.disabled === true ? PropertyType.String : false;
    }

    return false;
  }

  createFilterFromGroup(groupClause, filterValue, includesFilterValue) {
    const groupDataset = groupClause.dataset;
    const groupProperties = pick(groupClause, insightsGroupProperties);
    groupProperties[`defaultType`] = groupClause[`propertyType`];
    if (groupClause.propertyFilter && groupClause.propertyFilter.propertyType !== groupClause.propertyDefaultType) {
      groupProperties.typeCast = groupClause.propertyFilter.propertyType;
    }
    const joinType = groupClause.joinPropertyType;
    if (joinType) {
      groupProperties[`filterJoinType`] = joinType;

      if (joinType === PropertyType.List) {
        groupProperties[`listQuantifier`] = !includesFilterValue
          ? ListQuantifier.All
          : groupClause?.listQuantifier ?? ListQuantifier.Any;
      }
    }

    // None bucketing must evaluate to a String property type
    const groupType =
      this.isNoneBucketing(groupClause) ||
      groupProperties.typeCast ||
      groupProperties.propertyType ||
      groupProperties.customProperty?.propertyType;

    if ([`undefined`].includes(filterValue)) {
      if (groupType === `number`) {
        return this.createUndefinedFilter(groupDataset, groupProperties, includesFilterValue, `number`);
      }

      return this.createStringFilter(groupDataset, groupProperties, `undefined`, includesFilterValue, `string`);
    }

    switch (groupType) {
      case `string`:
        return this.createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      case `boolean`:
        return this.createBooleanFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      case `list`:
        // We cannot use list selectors to filter out empty lists, since they apply element-wise
        // Cast list property to string and do the comparison with $empty_list
        // query-api correctly resolves $empty_list to the right value
        if (filterValue === ConstantPropertyValues.EmptyList) {
          return this.createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
        } else {
          return this.createListFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
        }
      case `number`:
        return this.createNumberFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      case `object`:
        if (groupClause.listItemGroup) {
          return this.createListOfObjectsFilter(
            groupDataset,
            groupProperties,
            filterValue,
            includesFilterValue,
            groupClause,
          );
        }

        break;
      case `datetime`:
        if ([`day_of_week`, `hour_of_day`].includes(groupProperties.unit)) {
          const filter = this.createStringFilter(
            groupDataset,
            groupProperties,
            filterValue,
            includesFilterValue,
            groupClause,
          );
          filter[`filterDateUnit`] = groupProperties.unit;
          return filter;
        } else {
          return this.createDatetimeFilter(
            groupDataset,
            groupProperties,
            filterValue,
            this.queryResults.meta.time_period_settings,
            includesFilterValue,
            groupClause,
          );
        }
    }

    return null;
  }

  createUndefinedFilter(groupDataset, groupProperties, includesFilterValue, filterType = `string`) {
    return {
      ...groupProperties,
      filterOperator: includesFilterValue ? `is not set` : `is set`,
      filterType,
      dataset: groupDataset,
    };
  }

  createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause = null) {
    let filterOperator = includesFilterValue ? `equals` : `does not equal`;

    if (groupClause?.filterOperator) {
      filterOperator = includesFilterValue
        ? groupClause.filterOperator
        : STRING_OPERATOR_NEGATION_MAP[groupClause.filterOperator];
    }

    return {
      ...groupProperties,
      filterOperator,
      filterType: `string`,
      filterValue: groupClause?.filterValue ?? [filterValue],
      dataset: groupDataset,
    };
  }

  createBooleanFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause = null) {
    // Value Comparison case
    if (groupClause?.filterOperator) {
      return {
        ...groupProperties,
        filterOperator: includesFilterValue
          ? groupClause.filterOperator
          : BOOLEAN_OPERATOR_NEGATION_MAP[groupClause.filterOperator],
        filterType: `boolean`,
        dataset: groupDataset,
      };
    }

    const isTrue = !!JSON.parse(filterValue) === includesFilterValue;
    return {
      ...groupProperties,
      filterOperator: isTrue ? `is true` : `is false`,
      filterType: `boolean`,
      dataset: groupDataset,
    };
  }

  createListFilter(groupDataset, groupProperties, filterValue, includesFilterValue, _groupClause = null) {
    const filter = {
      ...groupProperties,
      dataset: groupDataset,
      filterValue,
      filterType: `list`,
      filterOperator: includesFilterValue ? `contains` : `does not contain`,
    };

    return filter;
  }

  createNumberFilter(groupDataset, groupProperties, value, includesFilterValue, groupClause = null) {
    // Value Comparison case
    if (groupClause?.filterOperator && (groupClause?.filterValue || groupClause?.filterValue === 0)) {
      return {
        ...groupProperties,
        filterType: `number`,
        dataset: groupDataset,
        filterOperator: includesFilterValue
          ? groupClause.filterOperator
          : NUMBER_OPERATOR_NEGATION_MAP[groupClause.filterOperator],
        filterValue: groupClause.filterValue,
      };
    }

    const {filterOperator, filterValue} = this.assignNumericFilterOperatorAndValue(value, includesFilterValue);

    return {
      ...groupProperties,
      filterType: `number`,
      dataset: groupDataset,
      filterOperator,
      filterValue,
    };
  }

  createListOfObjectsFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause) {
    // group is a list of objects property breakdown
    const listItemFilter = this.createFilterFromGroup(
      new GroupClause({
        ...groupClause.listItemGroup,
        resourceType: groupClause.resourceType,
        value: groupClause.listItemGroup.propertyName,
      }),
      filterValue,
      includesFilterValue,
    );

    // list of objects filter is most like a boolean filter
    const filter = this.createBooleanFilter(groupDataset, groupProperties, true, true, groupClause);
    filter.filterType = PropertyType.Object;
    filter.filterOperator = BooleanOperator.True;
    filter.listItemFilters = [listItemFilter];
    filter.listQuantifier = includesFilterValue ? ListQuantifier.Any : ListQuantifier.All;

    return filter;
  }

  assignNumericFilterOperatorAndValue(value, includesFilterValue) {
    const numericValue = typeof value === `number` ? value : parseNumber(value);
    const numberRange = typeof value === `number` ? value : parseNumberRange(value);
    let filterOperator;
    let filterValue;

    if (!Array.isArray(numberRange) && numericValue === null) {
      return null;
    }

    if (numericValue !== null) {
      filterOperator = includesFilterValue ? NumberOperator.EqualTo : NumberOperator.NotEqualTo;
      filterValue = numericValue;
    } else {
      if (Number.isFinite(numberRange[0]) && Number.isFinite(numberRange[1])) {
        filterOperator = includesFilterValue ? NumberOperator.Between : NumberOperator.NotBetween;
        filterValue = numberRange;
      } else if (Number.isFinite(numberRange[1])) {
        filterOperator = includesFilterValue ? NumberOperator.LessThan : NumberOperator.AtLeast;
        filterValue = numberRange[1];
      } else {
        filterOperator = includesFilterValue ? NumberOperator.AtLeast : NumberOperator.LessThan;
        filterValue = numberRange[0];
      }
    }

    return {filterOperator, filterValue};
  }

  createDatetimeFilter(
    groupDataset,
    groupProperties,
    filterValue,
    timePeriodSettings,
    includesFilterValue,
    groupClause = null,
  ) {
    const unit = groupProperties.unit;
    const quarterStartMonth =
      timePeriodSettings && timePeriodSettings.quarter_start_month ? timePeriodSettings.quarter_start_month : 0;
    let dateRange = parseDatetimeRange(filterValue, unit, quarterStartMonth);
    let defaultAndFilterType = `datetime`;
    let filterOperator;

    if (unit === TimeUnit.Hour) {
      dateRange = getCalendarDate(dateRange[0]);
      defaultAndFilterType = `string`;
    }
    if (groupClause?.filterOperator) {
      filterOperator = includesFilterValue
        ? groupClause.filterOperator
        : INSIGHTS_DATE_RANGE_NEGATION_MAPPING[groupClause.filterOperator];
    }

    if (dateRange) {
      return {
        ...groupProperties,
        defaultType: groupClause?.defaultType ?? defaultAndFilterType,
        filterOperator: filterOperator ?? this.parseDatetimeFilterOperator(unit, includesFilterValue),
        filterType: groupClause?.filterType ?? defaultAndFilterType,
        filterValue: groupClause?.filterValue ?? dateRange,
        filterDateUnit: unit,
        dataset: groupDataset,
      };
    }

    return null;
  }

  parseDatetimeFilterOperator(unit, includesFilterValue) {
    if (unit === `hour`) {
      return includesFilterValue ? `contains` : `does not contain`;
    } else if (unit === `day`) {
      return includesFilterValue ? `was on` : `was not on`;
    } else {
      return includesFilterValue ? `was between` : `was not between`;
    }
  }

  track(eventName: ProdTrackingEvent, props?: {[key: string]: any}) {
    emitTrackEvent(this, {eventName, props: props || {}});
  }

  trackSort(group, sortConfig, colIdx?: number) {
    const reportProperties = this.state.report.toTrackingData();
    const eventProperties = {};
    switch (group) {
      case `colSort`:
      case `left`:
        eventProperties[`sort by`] = sortConfig.colSortAttrs[colIdx].sortBy;
        eventProperties[`sort order`] = sortConfig.colSortAttrs[colIdx].sortOrder;
        eventProperties[`sort group`] = sortConfig.sortBy;
        eventProperties[`sort column index`] = colIdx;
        break;
      case `axisSort`:
      case `right`:
        eventProperties[`sort by`] = sortConfig.sortBy;
        eventProperties[`sort order`] = sortConfig.sortOrder;
        eventProperties[`sort group`] = `axis`;
        break;
    }
    this.track(ProdTrackingEvent.ChartSort, extend(reportProperties, eventProperties));
  }

  trackFilter(filter, projectId) {
    this.track(ProdTrackingEvent.PopoverAddFilter, {
      'Property name': filter.filterValue,
      'Property data type': filter.propertyType,
      'Resource type': filter.resourceType,
      'Filter operator': filter.filterOperator,
      'Filter value': filter.filterValue,
      project_id: projectId,
      source: `insights-result`,
    });
  }

  getClausesFromSegmentPath(path: string[]) {
    // eslint-disable-next-line
    const {show: showMetadata, formula: formulaMetadata} = this.queryResults?.meta.report_sections;
    const {clauses: showClauses} = this.state.report.sections.show;
    const {clauses: formulaClauses} = this.state.report.sections.formula;

    if ((!showMetadata && !formulaMetadata) || !path) {
      return null;
    }

    const metadataClauses = formulaMetadata ?? showMetadata;
    const findMatchingMetric = (metadata) => metadata.metric_key === path[0];
    const clauseIdx = metadataClauses.findIndex(
      (item) => findMatchingMetric(item) || item[`sub_queries`]?.some((item) => findMatchingMetric(item)),
    );

    /** for formulas we pick all clauses that match the formula value */
    if (formulaMetadata && formulaClauses[clauseIdx]) {
      const formulaValue = formulaClauses[clauseIdx].value;
      const matchingMetricKeys = new Set(formulaValue.match(/[A-Z]{1,}/g));
      const clauses = showClauses.filter((clause) => matchingMetricKeys.has(clause.formulaLabel));

      return clauses.length ? clauses : null;
    }

    return showClauses[clauseIdx] ? [showClauses[clauseIdx]] : null;
  }

  segmentUntranslatableReason(path) {
    const clauses = this.getClausesFromSegmentPath(path);
    const {timeUnit} = this.helpers.getDisplayOptions();
    const isSubDayTimeUnit = timeUnit === TimeUnit.Hour || timeUnit === TimeUnit.Minute;

    if (clauses.some((clause) => Boolean(clause.rolling)) && isSubDayTimeUnit) {
      return `${capitalize(timeUnit)} granularity not supported.`;
    }

    if (!Array.isArray(clauses)) {
      return `Clause not found.`;
    }

    if (clauses.some((clause) => clause.resourceType === InsightsResourceType.Cohorts)) {
      return `Cohorts not supported.`;
    }

    if (clauses.some((clause) => clause.resourceType === InsightsResourceType.People && clause.property)) {
      return `Profile analysis property aggregation not supported.`;
    }

    if (this.hasMultipleListOfObjectsBreakdowns()) {
      return `Multiple list of objects breakdowns not supported.`;
    }

    if (this.helpers.someSegmentHasMultiAttribution(path)) {
      return MultiAttributionDisabledTooltipText;
    }

    return null;
  }

  segmentUntranslatableReasonEvents(path) {
    const clauses = this.getClausesFromSegmentPath(path);
    const {timeUnit} = this.helpers.getDisplayOptions();
    const isSubDayTimeUnit = timeUnit === TimeUnit.Hour || timeUnit === TimeUnit.Minute;

    if (clauses.some((clause) => Boolean(clause.rolling)) && isSubDayTimeUnit) {
      return `${capitalize(timeUnit)} granularity not supported.`;
    }

    if (!Array.isArray(clauses)) {
      return `Clause not found.`;
    }

    if (clauses.some((clause) => clause.resourceType === InsightsResourceType.Cohorts)) {
      return `Cohorts not supported.`;
    }

    if (clauses.some((clause) => clause.resourceType === InsightsResourceType.People)) {
      return `Profiles not supported.`;
    }

    if (clauses.some((clause) => (clause.filter || []).some((filter) => filter.value === `$cohorts`))) {
      return `Inline cohort filters are not supported.`;
    }

    if (this.helpers.someSegmentHasMultiAttribution(path)) {
      return MultiAttributionDisabledTooltipText;
    }

    if (this.helpers.isBehavioralSegmentation()) {
      return `Behaviors not supported.`;
    }

    if (this.hasMultipleListOfObjectsBreakdowns()) {
      return `Multiple list of objects breakdowns not supported.`;
    }

    return null;
  }

  addClause(type, clauseData) {
    const report = this.state.report;
    const section = report.sections[type];
    const clause = Clause.create(type, clauseData);
    const sections = report.sections.replaceSection(section.appendClause(clause));
    report.sections = sections;
    this.update({report});
    this.dispatchUpdate({sections: sections.toBookmarkData()});
  }

  handleTablePopoverAction: PopoverActionCallback = ({
    segment,
    action,
    isTimeComparisonSegment,
    color,
    columnIndex,
  }) => {
    switch (action) {
      case PopoverOption.Filter:
        this.handleTablePopoverFilter(segment, {columnIndex});
        break;
      case PopoverOption.Exclude:
        this.handleTablePopoverExclude(segment, {columnIndex});
        break;
      case PopoverOption.ViewUsers: {
        this.handleViewUsersForSegment(segment, {
          forTimeComparisonSegment: isTimeComparisonSegment,
          color,
        });
        break;
      }
      case PopoverOption.ViewEvents:
        this.handleViewEventsForSegment(segment);
        break;
    }
  };

  handleTablePopoverFilter(segment: string[], {columnIndex = null}: {columnIndex?: number} = {}) {
    let filters = this.getFiltersFromPopoverSegments(segment);
    // Don't apply filters for segments to the right of where the user clicked.
    if (columnIndex !== null) {
      filters = filters.slice(0, columnIndex + 1);
    }

    filters.forEach((filterIn) => {
      this.addClause(FilterClause.TYPE, filterIn);
      this.trackFilter(filterIn, this.helpers.getProjectId());
    });
  }
  handleRCAClicked(pointData) {
    const clauseData = this.getLineChartPointClauseData(pointData);
    const url = `${getReportUrl(`boards`, {
      projectId: this.helpers.getProjectId(),
      workspaceId: this.helpers.getWorkspaceId(),
    })}#rca=${this.attr(`rca-source-id`)}&date=${clauseData.dateRange.to}&type=bookmark`;
    window.open(url);
  }

  handleTablePopoverExclude(segment, {columnIndex = null}: {columnIndex?: number} = {}) {
    let targetFilterValue;
    const groupClauses = this.state.report.sections.group.clauses;
    const groupCount = groupClauses.length;

    /** when a columnIndex is set, we want to exclude a specific value in a table, instead of just excluding the leaf segment */
    if (columnIndex !== null) {
      targetFilterValue = segment[columnIndex + 1]; // account for the metric segmentation path
    } else {
      //Bar charts will have "all" as the last segment value so we want to avoid grabbing that and instead grab the value just prior to it
      targetFilterValue =
        segment[segment.length - 1] === `all` ? segment[segment.length - 2] : segment[segment.length - 1];
    }

    const columnTarget = columnIndex ?? groupCount - 1;
    let groupClause = groupClauses[columnTarget];
    //We need to use metaGroupClause because it has the segmentLabel attribute that allows us to uniquely identify the relevant groupClause
    const metaGroupClause = this.queryResults.meta.report_sections.group[columnTarget].bookmark;
    const hasCohortsClauses = !!this.state.report.sections.cohorts.clauses.length;
    const isCustomStringGrouping = Array.isArray(metaGroupClause.customGroups);
    const isCustomNumericGrouping = !!metaGroupClause.customBucket?.groups;

    if (groupCount > 0) {
      if (metaGroupClause && Array.isArray(metaGroupClause)) {
        metaGroupClause.forEach((clause, clauseIdx) => {
          if (clause.segmentLabel === targetFilterValue) {
            groupClause = groupClause.clauses[clauseIdx];
          }
        });
      } else if (metaGroupClause && isCustomStringGrouping) {
        // Handle the customGroups format for Customize Segments - do not handle the undefined case here
        const filteredGroupClause = this.createGroupFromCustomGroups(
          metaGroupClause.customGroups,
          groupClause,
          targetFilterValue,
        );
        this.addClause(
          FilterClause.TYPE,
          this.createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, false),
        );
        this.trackFilter(filteredGroupClause, this.helpers.getProjectId());
      } else if (metaGroupClause && isCustomNumericGrouping && targetFilterValue === NON_NUMERIC_VALUES_SEGMENT) {
        const numFilter = Object.assign({}, groupClause, {
          filterValue: groupClause.value,
          filterOperator: StringOperator.Set,
        });
        this.addClause(FilterClause.TYPE, this.createFilterFromGroup(numFilter, numFilter.filterValue, true));
        this.trackFilter(numFilter, this.helpers.getProjectId());
      }
      const filterOut = this.createFilterFromGroup(groupClause, targetFilterValue, false);
      // Avoid creating duplicate filters
      if (
        (filterOut && !isCustomStringGrouping) ||
        !(isCustomNumericGrouping && targetFilterValue === NON_NUMERIC_VALUES_SEGMENT)
      ) {
        this.addClause(FilterClause.TYPE, filterOut);
        this.trackFilter(filterOut, this.helpers.getProjectId());
      }
      return;
    }

    if (hasCohortsClauses) {
      const filterOut = this.createFilterFromCohort(segment[1], false);
      if (filterOut) {
        this.addClause(FilterClause.TYPE, filterOut);
      }
    }
  }

  getLineChartPointClauseData(pointData) {
    const timestamp = pointData.isComparisonItem
      ? // it's either time comparison or value comparison
        (pointData.liftComparisonStat || pointData.timeComparisonStat).timestamp
      : pointData.stat.timestamp;
    const clause = pointData.metricClauseData;
    const isSubDayTimeUnit = pointData.timeUnit === TimeUnit.Hour || pointData.timeUnit === TimeUnit.Minute;
    const timeWindowUnit = isSubDayTimeUnit ? TimeUnit.Day : pointData.timeUnit;
    let calculationType;
    if (clause.rolling) {
      calculationType = CalculationTypes.Rolling;
    } else if (clause.cumulative) {
      calculationType = CalculationTypes.Cumulative;
    }
    let dateRangeStr = formatDate(timestamp, {unit: pointData.timeUnit});
    let filters = [];

    if (XAU_MATH_TYPE_TIME_UNITS.has(clause.math)) {
      const {fromDate, toDate} = timestampToXauDateRange(timestamp, {
        unit: pointData.timeUnit,
        operator: clause.math,
        isLastInterval: pointData.isLastInterval,
        lastIntervalToDate: pointData.stat.dateRange.toDate,
      });

      return {
        calculationType,
        filters,
        dateRange: new DateRange({
          type: DateRangeType.Between,
          from: getCalendarDate(fromDate ?? toDate),
          to: getCalendarDate(toDate),
        }),
      };
    }

    if (isSubDayTimeUnit) {
      // for hourly line charts, we need to filter on `hour_of_day`
      dateRangeStr = formatDate(timestamp, {unit: TimeUnit.Day});
      filters = [
        new PropertyFilter({
          filterOperator: StringOperator.Contains,
          // pick out the date, hour & minute component of the date range
          filterValue: moment(timestamp)
            .format()
            .substr(0, pointData.timeUnit === TimeUnit.Minute ? 16 : 13),
          propertyDefaultType: PropertyType.Datetime,
          propertyName: MPProperties.Date,
          propertyType: PropertyType.String,
          resourceType: ResourceType.Event,
        }),
      ];
    }

    const dateFilter = this.createDatetimeFilter(
      clause.dataset,
      {unit: timeWindowUnit},
      dateRangeStr,
      this.queryResults?.meta.time_period_settings,
      true,
    );

    const dateRange = new DateRange({
      type: DateRangeType.Between,
      ...(Array.isArray(dateFilter.filterValue)
        ? {from: dateFilter.filterValue[0], to: dateFilter.filterValue[1]}
        : {from: dateFilter.filterValue, to: dateFilter.filterValue}),
    });

    return {calculationType, filters, dateRange};
  }

  handleLineChartZoomIn(pointData) {
    const {dateRange, filters} = this.getLineChartPointClauseData(pointData);
    const selectedTimeUnit = this.state.report.sections.time.clauses[0].unit;
    const selectedFilters = this.state.report.sections.filter;
    const updatedTimeUnit =
      OrderedStandardTimeUnits[OrderedStandardTimeUnits.findIndex((unit) => unit === selectedTimeUnit) - 1];

    const sections = this.state.report.sections
      .replaceSection(getTimeSection(dateRange, {overrideTimeUnit: updatedTimeUnit}))
      .replaceSection(
        getFilterSection(
          [...getPropertyFilters(selectedFilters.clauses), ...filters],
          getPropertyFiltersOperator(selectedFilters.determiner),
        ),
      )
      .toBookmarkData();

    this.dispatchUpdate({sections});
    this.track(ProdTrackingEvent.PopoverZoomIn, this.state.report.toTrackingData());
  }

  handleLineChartViewUsers(pointData) {
    const {displayOptions} = this.state.report;
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const clauseData = this.getLineChartPointClauseData(pointData);
    const {dateRange, calculationType} = clauseData;

    /** cumulative view specifies users up to the point in the chart that was clicked */
    if (displayOptions.analysis === AnalysisType.Cumulative || calculationType === CalculationTypes.Cumulative) {
      dateRange.from = getCalendarDate(this.queryResults.date_range.from_date);
    } else if (displayOptions.analysis === AnalysisType.Rolling || calculationType === CalculationTypes.Rolling) {
      const windowSize = displayOptions.rollingWindowSize;
      dateRange.from = getCalendarDate(
        moment(dateRange.from as AbsoluteDate).subtract(windowSize - 1, pointData.timeUnit),
      );
    }

    this.handleViewUsersForSegment(path, {
      dateRange,
      additionalFilters: clauseData.filters,
      forTimeComparisonSegment: pointData.isComparisonItem,
      color: pointData.titleColor,
    });
  }

  handleViewUsersForSegment(
    segment,
    {additionalFilters = [], dateRange = null, forTimeComparisonSegment = false, color = ``} = {},
  ) {
    const showClauses = this.getClausesFromSegmentPath(segment);

    if (showClauses === null) {
      /** @todo: [pablo] Do some error logging here */
      return;
    }

    const cohort = createNestedCohort(
      showClauses.map((clause) =>
        this.getCohortForSegmentShowClause(clause, segment, {
          additionalFilters,
          dateRange,
          forTimeComparisonSegment,
        }),
      ),
    );

    // TODO: hansli proper name & description for the cohort
    cohort.name = `[Untitled] - ${segment[0]}`;
    cohort.description = `Insight Report: Untitled event: ${segment[0]}`;

    const params = {
      include_all_users: !this.state.report.sections.show.isPeopleOnlyQuery(),
      serialized_cohort: encodeURIComponent(JSON.stringify(cohort.serialize())),
      query_source: `View Users`,
      ...(cohort.dataGroupId
        ? {
            data_group_id: cohort.dataGroupId,
          }
        : {}),
    };

    this.track(ProdTrackingEvent.PopoverViewUsers, this.state.report.toTrackingData());

    const dataGroupName = this.helpers.getActiveDataGroupDisplayName();
    openUserSidebar(
      {
        'include-all-users': params.include_all_users,
        'report-name': ReportName.Insights,
        segments: segment,
        ...(dataGroupName
          ? {
              'data-group-name': dataGroupName,
            }
          : {}),
        ...(dateRange
          ? {
              'from-date': dateRange.from,
              'to-date': dateRange.to,
            }
          : {}),
        color,
      },
      {
        cohort,
        cohortDetails: [
          {
            cohort,
            label: `Create Cohort`,
            type: `insights`,
            warning: ``,
          },
        ],
      },
    );
  }

  getCohortForSegmentShowClause(
    showClause,
    segment,
    {additionalFilters = null, dateRange = null, forTimeComparisonSegment = null} = {},
  ) {
    const filterClauses = this.state.report.sections.filter.clauses;

    let combinedFilterClauses = [];

    // for numerical breakdowns, ranges in the form of A - B, are exclusive on B so we must transform this filter
    // from between to an exclusive on the right filter
    const segmentFilters = this.getFiltersFromPopoverSegments(segment, {
      makeNumericRangesExclusive: true,
    }).map((filter) => new FilterClause(filter));

    combinedFilterClauses.push(...segmentFilters);

    // Add global filters
    if (filterClauses.length) {
      combinedFilterClauses = [...combinedFilterClauses, ...filterClauses];
    }

    const [entry] = getEntries([showClause], this.dataGroups);
    let frequency = null;

    // for top events, replace the selected top event event value
    if (entry.event.value === SPECIAL_EVENTS.TOP_EVENTS) {
      const parsedEventName = segment[0].split(` - `)[0];
      entry.event.value = findEventDefinitionByDisplayName(parsedEventName)?.name ?? parsedEventName;
      entry.event.label = parsedEventName;
    }

    if (entry.aggregationOperator === PRIMITIVE_MATH_TYPES.HISTOGRAM) {
      const lastSegment = segment[segment.length - 1];
      frequency = new FilterClause(this.createNumberFilter(null, {}, lastSegment, true)).toPropertyFilter();
    }

    const filters = [...(additionalFilters ?? []), ...getPropertyFilters(combinedFilterClauses)];

    const timeComparisonDateRange = this.queryResults.time_comparison?.date_range;
    // handle compare to past
    if (!dateRange && timeComparisonDateRange && forTimeComparisonSegment) {
      const {from_date: from, to_date: to} = timeComparisonDateRange;
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: getCalendarDate(from),
        to: getCalendarDate(to),
      });
    }

    // bar being clicked, transform to absolute date range
    if (!dateRange && !forTimeComparisonSegment) {
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: getCalendarDate(this.queryResults.date_range.from_date),
        to: getCalendarDate(this.queryResults.date_range.to_date),
      });
    }

    return translateMetricToCohort(entry, {
      name: `${showClause.formulaLabel} - ${renameEvent(showClause.value.name)}`,
      dateRange,
      filters,
      keepUserAggregation: entry.aggregationOperator === PRIMITIVE_MATH_TYPES.HISTOGRAM,
      ...(frequency
        ? {
            frequency,
            frequencyExclusive: true,
          }
        : {}),
    });
  }

  handleLineChartViewEvents(pointData) {
    const {displayOptions} = this.state.report;
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const clauseData = this.getLineChartPointClauseData(pointData);
    const {dateRange, calculationType} = clauseData;

    // TODO: dedupe from view users usage
    /** cumulative view specifies events up to the point in the chart that was clicked */
    if (calculationType === CalculationTypes.Cumulative) {
      dateRange.from = getCalendarDate(this.queryResults.date_range.from_date);
    } else if (calculationType === CalculationTypes.Rolling) {
      const windowSize = displayOptions.rollingWindowSize;
      dateRange.from = getCalendarDate(
        moment(dateRange.from as AbsoluteDate).subtract(windowSize - 1, pointData.timeUnit),
      );
    }

    this.handleViewEventsForSegment(path, {
      dateRange,
      additionalFilters: clauseData.filters,
    });
  }

  handleViewEventsForSegment(segment: string[], {additionalFilters = null, dateRange = null} = {}) {
    const showClauses = this.getClausesFromSegmentPath(segment);
    const globalFilters = this.state.report.sections.filter.clauses.map((filterClause) => filterClause.propertyFilter);
    const filtersFromSegments = this.getFiltersFromPopoverSegments(segment).map(
      (filter) => new FilterClause(filter).propertyFilter,
    );
    const timeClause = this.state.report.sections.time.clauses[0];

    this.track(ProdTrackingEvent.PopoverViewEvents, this.state.report.toTrackingData());

    const url = getEventsReportUrl({
      projectId: this.helpers.getProjectId(),
      workspaceId: this.helpers.getWorkspaceId(),
      entries: convertSegmentForEventsInspector(showClauses),
      dateRange: dateRange || getDateRange([timeClause]),
      filters: [].concat(globalFilters, additionalFilters, filtersFromSegments).filter(Boolean),
    });
    if (this.helpers.shouldOpenSidebar()) {
      openEventsSidebar({url});
    } else {
      window.open(url, `_blank`);
    }
  }

  hasMultipleListOfObjectsBreakdowns() {
    const groupClauses: GroupClause[] = this.state.report.sections.group.clauses;

    const numListOfObjects = groupClauses.filter((clause) => !!clause.listItemGroup).length;

    return numListOfObjects > 1;
  }

  dispatchUpdate(delta: Partial<InsightsBookmarkParams>) {
    const event: UpdateEvent = new CustomEvent(`update`, {detail: {delta}});
    this.dispatchEvent(event);
  }
}

export const InsightsChartDisplayEl = defineAndMakeJsxFactory<ChartDisplayJsxProps>(
  `insights-chart-display`,
  ChartDisplay,
);
export const InsightsChartDisplayReact = panel2react<ChartDisplayJsxProps>(`insights-chart-display`, ChartDisplay);
